import { __awaiter } from "tslib";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { ENRICH_DRAWER_SECTION, useUserRoutePermissions } from '@groupby/enrich-console';
import { useAppDispatch } from '@/store/hooks';
import { debounce } from '@/utils/debounce';
import { useActiveModelPath } from '@/routing/hooks/utils';
import { PageIdentifier, Role, Routing, SectionPath, } from '@/constants';
import { LocalizedMessage, useLocalization } from '@/localization';
import { dashboardTitleToLocalizedMessageKey, normalizeDashboardTitle } from '@/search-for-retail/analytics';
import { addError } from '@/store/slices/notificationsSlice';
import { useActiveUser } from '@/platform-services/auth';
import { useSessionAreaName, useSessionCollectionName } from '@/platform-services/session';
import { generateCollectionAreaUrl } from '@/search-for-retail/utilities';
import { filterRoutesByUserAccess, patchDrawerRoutesWithFields } from './utils';
import { useAuthToken } from '@/auth/hooks/authentication';
import { TitleBadge } from '@/search-for-retail/generic/title-badge/title-badge.component';
import { StyledBetaWrapper } from './drawer/navigation-drawer.styles';
import { NavigationType } from '@/navigation/types';
import { FeatureFlag, useFeatureFlagsProvider } from '@/search-for-retail/feature-flags';
import { excludedDashboardsFromRoute } from '@/search-for-retail/analytics/analytics.utils';
import { logError } from '@/utils/logger';
const NavigationContext = createContext({
    sections: [],
    dashboards: [],
    isDrawerOpen: true,
    navigationTypeOpen: NavigationType.PRIMARY,
    setVisibleRouteSection: (_section) => {
    },
    setDrawerOpen: (_override, _open) => {
    },
    setNavigationType: (_navigationType) => {
    },
});
const isPathInRoutes = (routes, modelPath) => routes.some((route) => {
    var _a;
    const hasSubroutes = !!((_a = route.subroutes) === null || _a === void 0 ? void 0 : _a.length);
    return route.path === modelPath || (hasSubroutes && isPathInRoutes(route.subroutes, modelPath));
});
export const NavigationProvider = ({ dashboards, drawerConfig, children, }) => {
    var _a;
    const dispatch = useAppDispatch();
    const { featureFlagsService } = useFeatureFlagsProvider();
    const { getDrawerRoutes } = useUserRoutePermissions();
    const activeUser = useActiveUser();
    const collectionName = useSessionCollectionName();
    const areaName = useSessionAreaName();
    const token = useAuthToken();
    const { formatMessage: t } = useLocalization();
    const isNewSearchPerformanceDashboardEnabled = featureFlagsService.isFeatureEnabled(FeatureFlag.EnableNewSearchPerformanceDashboard);
    const isPipelineHealthEnabled = featureFlagsService.isFeatureEnabled(FeatureFlag.EnablePipelineHealth);
    const modelPath = useActiveModelPath();
    // local state
    const [sections, setSections] = useState((_a = drawerConfig === null || drawerConfig === void 0 ? void 0 : drawerConfig.sections) === null || _a === void 0 ? void 0 : _a.map((section) => {
        var _a;
        return (Object.assign(Object.assign({}, section), { categories: (_a = section === null || section === void 0 ? void 0 : section.categories) === null || _a === void 0 ? void 0 : _a.map((category) => (Object.assign(Object.assign({}, category), { routes: filterRoutesByUserAccess(category.routes, activeUser) }))) }));
    }));
    // Mobile-first: Initialize drawer as collapsed on mobile screens (< 900px)
    const [isDrawerOpen, setIsDrawerOpen] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth >= 900;
        }
        return true;
    });
    const [navigationTypeOpen, setNavigationTypeOpen] = useState(NavigationType.PRIMARY);
    const setDrawerOpen = useCallback((override, openState) => {
        setIsDrawerOpen((prevValue) => (override ? openState : !prevValue));
    }, []);
    const setNavigationType = useCallback((navigationType) => {
        setNavigationTypeOpen(navigationType);
    }, []);
    const handleResize = useMemo(() => {
        const fn = () => {
            const isMobile = window.innerWidth < 900;
            if (isMobile && isDrawerOpen) {
                setIsDrawerOpen(false);
            }
        };
        return debounce(fn, 200);
    }, [isDrawerOpen]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            handleResize.clear();
        };
    }, [handleResize]);
    const [visibleRouteSection, setVisibleRouteSection] = useState(sections === null || sections === void 0 ? void 0 : sections.find((section) => { var _a; return !!((_a = section === null || section === void 0 ? void 0 : section.categories) === null || _a === void 0 ? void 0 : _a.find((category) => isPathInRoutes(category.routes, modelPath))); }));
    const setRoutesByLabel = useCallback((label, categories, fieldsToPatch) => {
        const securedCategories = categories === null || categories === void 0 ? void 0 : categories.map((category) => (Object.assign(Object.assign({}, category), { routes: patchDrawerRoutesWithFields(filterRoutesByUserAccess(category.routes, activeUser), fieldsToPatch) }))).filter(({ routes }) => !!routes.length);
        setSections((prevSections) => prevSections === null || prevSections === void 0 ? void 0 : prevSections.map((section) => {
            if (section.label === label) {
                return Object.assign(Object.assign({}, section), { categories: securedCategories });
            }
            return section;
        }));
    }, [activeUser]);
    // fetch enrich categories
    useEffect(() => {
        const generateEnrichDrawerCategories = () => __awaiter(void 0, void 0, void 0, function* () {
            const enrichCategories = yield getDrawerRoutes(featureFlagsService);
            setRoutesByLabel(ENRICH_DRAWER_SECTION.label, enrichCategories, { sectionPath: SectionPath.ENRICH });
        });
        if (token) {
            void generateEnrichDrawerCategories();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setRoutesByLabel, token]);
    const betaBadge = useMemo(() => (<TitleBadge label={t({ key: 'BETA' })} size="small"/>), [t]);
    const searchPerformanceLabel = useMemo(() => (<StyledBetaWrapper>
      <LocalizedMessage messageKey="SEARCH_PERFORMANCE"/>
      {betaBadge}
    </StyledBetaWrapper>), [betaBadge]);
    const dataQualityRoutes = [{
            name: PageIdentifier.SEARCH_DATA_QUALITY,
            path: Routing.ROUTE_PATHS[PageIdentifier.SEARCH_DATA_QUALITY],
            sectionPath: SectionPath.ANALYTICS,
            label: 'SEARCH_DATA_QUALITY',
            roles: [Role.ADMIN, Role.MERCHANDISING],
        }];
    const pipelineHealthRoutes = isPipelineHealthEnabled ? [{
            name: PageIdentifier.PIPELINE_HEALTH,
            path: Routing.ROUTE_PATHS[PageIdentifier.PIPELINE_HEALTH],
            sectionPath: SectionPath.ANALYTICS,
            label: 'PIPELINE_HEALTH',
            roles: [Role.ADMIN, Role.MERCHANDISING],
        }] : [];
    const searchPerformanceRoute = {
        name: PageIdentifier.SEARCH_PERFORMANCE,
        path: Routing.ROUTE_PATHS[PageIdentifier.SEARCH_PERFORMANCE],
        sectionPath: SectionPath.ANALYTICS,
        label: 'SEARCH_PERFORMANCE',
        customLabel: searchPerformanceLabel,
        roles: [Role.ADMIN, Role.MERCHANDISING],
    };
    const routes = [
        ...dataQualityRoutes,
        ...pipelineHealthRoutes,
    ];
    const drawerCategory = {
        label: 'DRAWER_CATEGORY_CHECK_SITE_HEALTH',
        routes,
    };
    // build analytics categories
    useEffect(() => {
        const action = () => {
            var _a;
            try {
                const url = generateCollectionAreaUrl(collectionName, PageIdentifier.ANALYTICS, areaName);
                const embeddedEnhancePerformanceDashboards = dashboards.filter((dashboard) => !excludedDashboardsFromRoute.includes(dashboard.title));
                const categories = [
                    {
                        label: 'DRAWER_CATEGORY_ENHANCE_PERFORMANCE',
                        routes: [
                            ...(isNewSearchPerformanceDashboardEnabled ? [Object.assign({}, searchPerformanceRoute)] : []),
                            ...((_a = embeddedEnhancePerformanceDashboards === null || embeddedEnhancePerformanceDashboards === void 0 ? void 0 : embeddedEnhancePerformanceDashboards.map((dashboard) => ({
                                isDynamicRoute: true,
                                name: PageIdentifier.ANALYTICS,
                                label: dashboardTitleToLocalizedMessageKey(dashboard.title),
                                path: `${Routing.MERCHANDISING_PATH}/analytics${url}${areaName ? `/${normalizeDashboardTitle(dashboard.title)}` : ''}`,
                                sectionPath: SectionPath.ANALYTICS,
                                roles: [Role.MERCHANDISING],
                                params: { dashboard },
                            }))) !== null && _a !== void 0 ? _a : []),
                        ],
                    },
                    Object.assign({}, drawerCategory),
                ];
                setRoutesByLabel('ANALYTICS', categories);
                if ((visibleRouteSection === null || visibleRouteSection === void 0 ? void 0 : visibleRouteSection.label) === 'ANALYTICS') {
                    const securedCategories = categories === null || categories === void 0 ? void 0 : categories.map((category) => (Object.assign(Object.assign({}, category), { routes: filterRoutesByUserAccess(category.routes, activeUser) }))).filter(({ routes }) => !!routes.length);
                    setVisibleRouteSection(Object.assign(Object.assign({}, visibleRouteSection), { categories: securedCategories }));
                }
            }
            catch (err) {
                const errorMessage = 'LOAD_ANALYTICS_ERROR';
                logError(errorMessage, err);
                dispatch(addError({ key: errorMessage }));
            }
        };
        void action();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dashboards, collectionName, areaName, dispatch, setRoutesByLabel]);
    return (<NavigationContext.Provider value={{
            sections,
            dashboards,
            visibleRouteSection,
            isDrawerOpen,
            navigationTypeOpen,
            setVisibleRouteSection,
            setDrawerOpen,
            setNavigationType,
        }}>
      {children}
    </NavigationContext.Provider>);
};
export default NavigationProvider;
export const useNavigation = () => useContext(NavigationContext);
